import { Form, Row } from "antd"
import {
  Alert,
  Card,
  DatePicker,
  Group,
  Icon,
  Legend,
  Modal,
  notification,
  RadioButtons,
  SelectInput,
  Tag,
  Text,
  TextInput,
  Tooltip,
  UploadDragger,
} from "components/common"
import { DATE_FORMAT } from "constants"
import {
  useLazyGetClassificationQuery,
  useLazyGetProjectsByCityQuery,
  useLazyGetProjectsQuery,
  useLazyGetTaskTypeQuery,
} from "features/shared/api"
import {
  useAddTaskMutation,
  useLazyGetCompanyOfficesQuery,
  useLazyGetOfficeCoordinatesQuery,
  useLazyGetTodosCountQuery,
  useUpdateTaskMutation,
} from "features/tasks/api"
import { t } from "i18next"
import { useEffect, useMemo, useState } from "react"
import { ACTION_TYPES } from "utils/gtm/gtmEventsData"

import cx from "clsx"
import Flex from "components/common/flex"
import TimeRangeSelector from "components/common/timeRangePicker/timeRangePicker"
import { IconText } from "components/custom"
import { configs } from "configs"
import { HH_MM_SS_FORMAT, YYYY_MM_DD_FORMAT } from "constants"
import dayjs from "dayjs"
import { useLazyGetCalenderEventsQuery, useLazyGetEventsForCalendarQuery } from "features/calendar/api"
import { useLazyGetLeadDetailsQuery } from "features/leads/api"
import { schemaRules } from "features/shared/utils"
import { OpenLocationCode } from "open-location-code"
import { useSelector } from "react-redux"
import { Link } from "react-router-dom"
import { mapKeysToValues } from "utils"
import { dateTimeFunction } from "utils/date"
import DateTime from "utils/dateTime"
import { fireEvent } from "utils/gtm/gtmEventHandler"
import theme from "utils/themeVars"
import VoiceComment from "../voiceComment/voiceComment"
import { getAddTaskInitialValues } from "./addTaskInitialValues"
import getTaskValidationSchema from "./addTaskValidationSchema"
import ClientDetailBar from "./components/clientDetailBar/clientDetailBar"
import plusCodeImage from "./components/images/plusCodeCopy.png"
import styles from "./index.module.less"
import MeetingVerificationAttachmentUploader from "./meetingVerificationAttachmentUploader"

// leadDetailInfo?.inquiryStatusDetails?.taskStatusParent === 24     (TODO LATER Needs to be changed this check should be outside of this component, in Tasks and Todo Listing Page.)

export default function AddTaskForm({ lead, client, formRef, from = {}, isEditable }) {
  const privileges = useSelector(state => state.app.privileges)
  const userInfo = useSelector(state => state?.app?.user.info)

  let getCalenderEventsObj = {
    startTime: dayjs().startOf("day").unix(),
    endTime: dayjs().endOf("day").unix(),
    userId: !!userInfo?.teamType === "support" ? lead?.User?.id : userInfo?.id,
  }

  const [getTaskTypes, { data: taskTypes, isFetching: isFetchingTaskTypes }] = useLazyGetTaskTypeQuery()
  const [getLeadDetails, { data: leadDetailInfo, isFetching: isFetchingLead }] = useLazyGetLeadDetailsQuery()
  // const [getCells, { data: cellNumberArray }] = useLazyGetCellNumberQuery(lead?.clientId)
  const [getProjects, { data: projects }] = useLazyGetProjectsQuery()
  const [getClassifications, { data: classifications }] = useLazyGetClassificationQuery()
  const [getCalendarEvents, { data: getCalendarEventsList }] = useLazyGetCalenderEventsQuery()
  const [getTodosCount, { data: todosCount }] = useLazyGetTodosCountQuery()
  const [getSalesEvents, { data: salesEvents, isFetching: isFetchingsSaleEvents }] = useLazyGetEventsForCalendarQuery()
  const [addTask, { isLoading, isError, error, reset: resetErrorForAddTask }] = useAddTaskMutation()
  const [getCompanyOffices, { data: companyOffice, isFetching: isCompanyOfficesFetching, error: companyError }] =
    useLazyGetCompanyOfficesQuery()
  const [getProjectsByCity, { data: projectsByCity, isFetching: isFetchingProjectsByCity, error: ProjectsError }] =
    useLazyGetProjectsByCityQuery()
  const [getCoordinates, { data: coordinates, isFetching: isFetchingCoordinates, error: coordinatesError }] =
    useLazyGetOfficeCoordinatesQuery()

  const [
    updateTask,
    {
      isLoading: isLoadingForUpdateTask,
      isError: isErrorForUpdateTask,
      error: errorForUpdateTask,
      reset: resetErrorForUpdateTask,
    },
  ] = useUpdateTaskMutation()

  const [form] = Form.useForm()
  const rules = schemaRules(getTaskValidationSchema())
  const { setFieldsValue, setFieldValue, getFieldError } = form
  const task = Form.useWatch("task", form)
  const subTask = Form.useWatch("subTask", form) || null
  const subofSubTask = Form.useWatch("subofSubTask", form)
  const verifyMeeting = Form.useWatch("verifyMeeting", form)
  const project = Form.useWatch("project", form)
  const attachment = Form.useWatch("attachment", form)
  const nextTask = Form.useWatch("nextTask", form)
  const dateSlot = Form.useWatch("dateSlot", form)
  const timeSlot = Form.useWatch("timeSlot", form)
  const deadline = Form.useWatch("deadLine", form)
  const salesEvent = Form.useWatch("salesEvent", form)
  const plusCode = Form.useWatch("plusCode", form)
  const completionDate = Form.useWatch("completionDate", form)
  const [busySlotVisible, setBusySlotVisible] = useState(false)
  const [disableSaleEvent, setDisableSaleEvent] = useState(false)
  let recorderControls = null

  const tasksTypesMap = useMemo(() => {
    return mapKeysToValues(taskTypes?.leadStatuses)
  }, [taskTypes])

  const taskChildrenMap = useMemo(() => {
    return mapKeysToValues(taskTypes?.leadStatuses?.find(e => e?.id === task)?.children)
  }, [task, taskTypes])

  const nextTasksMap = useMemo(() => {
    return mapKeysToValues(taskTypes?.nextStatuses)
  }, [taskTypes])

  const renderStartEndTime = useMemo(
    () =>
      [
        taskChildrenMap["video_meeting"],
        taskChildrenMap["walk_in"],
        taskChildrenMap["meeting_done"],
        taskChildrenMap["meeting_attempted"],
      ].includes(subTask),
    [subTask]
  )

  const attachmentVerificationRequired = [
    taskChildrenMap["video_meeting"],
    taskChildrenMap["walk_in"],
    taskChildrenMap["meeting_done"],
  ].includes(subTask)

  useEffect(() => {
    if (leadDetailInfo) {
      leadDetailInfo?.isLocationCompanyOffices && getCompanyOffices({ cityIds: userInfo?.City?.id?.toString() }, true)
      leadDetailInfo?.isLocationSiteProjects && getProjectsByCity(userInfo?.City?.id?.toString(), true)
      setFormValues()
    }
  }, [leadDetailInfo])

  useEffect(() => {
    if ((deadline || dateSlot) && userInfo && taskTypes) {
      const params = {
        deadline: dateSlot
          ? `${dateTimeFunction({ date: dateSlot?.startOf("day"), isUnix: true })},${dateTimeFunction({
              date: DateTime.getDaysAfterDateProvided(dateSlot?.startOf("day"), 6),
              isUnix: true,
            })}`
          : `${dateTimeFunction({ date: deadline?.startOf("day"), isUnix: true })},${dateTimeFunction({
              date: DateTime.getDaysAfterDateProvided(deadline?.startOf("day"), 6),
              isUnix: true,
            })}`,
        dateWiseSegregation: true,
      }
      getTodosCount({ userId: userInfo?.id, params })
    }
  }, [deadline, dateSlot, userInfo, taskTypes])

  useEffect(() => {
    if (subTask === taskChildrenMap["meeting_arranged"] || subTask === taskChildrenMap["meeting_pushed"]) {
      getCalendarEvents(getCalenderEventsObj)
    }
  }, [subTask])

  useEffect(() => {
    const saleEventId = leadDetailInfo?.inquiry?.Client?.SalesEventClients?.[0]?.SalesEvent?.id
    if (salesEvents?.saleEvents?.find(item => item.id == saleEventId)) {
      setFieldsValue({ salesEvent: saleEventId })
    }
  }, [salesEvents, leadDetailInfo])

  const onModalVisible = () => {
    if (subTask === taskChildrenMap["meeting_arranged"] || subTask === taskChildrenMap["meeting_pushed"]) {
      getCalendarEvents(getCalenderEventsObj)
    }
    getProjects()
    getClassifications()
    getLeadDetails({
      leadId: lead?.id || lead?.inquiryIds || lead,
      ...(isEditable === true && { detailsType: "edit_task" }),
    })
    getTaskTypes({
      params: {
        withNextStatuses: 1,
        leadType: lead?.interestedIn,
        ...(isEditable && { from: "edit_task" }),
        ...((userInfo?.AgencyConfiguration?.Platform?.platformKey !== "mubawab" ||
          userInfo?.AgencyConfiguration?.Platform?.platformKey !== "bproperty") &&
          (userInfo?.teamType === "sales" ||
            userInfo?.teamType === "secondarySales" ||
            userInfo?.teamType === "support") && { teamType: userInfo?.teamType }),
      },
    })
    getProjects()
    getClassifications()
    leadDetailInfo?.inquiry?.id === lead?.id && setFormValues()
    getSalesEvents({ afterTime: dayjs().toISOString(), eventTypes: "sale" })
  }
  const getController = constroller => {
    recorderControls = constroller
  }

  const setFormValues = () => {
    form.setFieldsValue(getAddTaskInitialValues({ ...leadDetailInfo, Project: lead?.Project, isEditable: isEditable }))
  }

  const handleSuggestedCommentClick = element => {
    form.setFieldsValue({ comment: element })
  }

  const onSubmit = async values => {
    const eventPayload = {
      client_name: lead?.Client?.name || client?.name,
      project_name: values?.project,
      task_type: taskTypes?.leadStatuses?.find(t => t?.id == values?.task)?.name,
      task_sub_type: taskChildren?.find(t => t?.id === values?.subTask)?.name,
      attachment: !!values?.attachment ? "yes" : "no",
      comments: values?.comment,
      next_task: taskTypes?.nextStatuses?.find(t => t?.id == values?.nextTask)?.name,
      lead_id: lead?.id,
    }

    values["completionDate"] = dayjs(
      values?.completionDate.format(YYYY_MM_DD_FORMAT) +
        " " +
        (!!renderStartEndTime
          ? values?.meetingTime?.[1]
            ? values?.meetingTime?.[1]?.format(HH_MM_SS_FORMAT)
            : ""
          : values?.completionTime?.[0]
          ? values?.completionTime?.[0]?.format(HH_MM_SS_FORMAT)
          : "")
    )
    values?.meetingTime &&
      (values["meetingTime"] = [
        dayjs(
          values?.completionDate.format(YYYY_MM_DD_FORMAT) +
            " " +
            (values?.meetingTime?.[0] ? values?.meetingTime?.[0].format(HH_MM_SS_FORMAT) : "")
        ),
        dayjs(
          values?.completionDate.format(YYYY_MM_DD_FORMAT) +
            " " +
            (values?.meetingTime?.[1] ? values?.meetingTime?.[1].format(HH_MM_SS_FORMAT) : "")
        ),
      ])
    values["deadLine"] = dayjs(
      values?.deadLine?.format(YYYY_MM_DD_FORMAT) +
        " " +
        (values?.deadLineTime?.[0] ? values?.deadLineTime?.[0].format(HH_MM_SS_FORMAT) : "")
    )
    if (values?.timeSlot) {
      const start = dayjs(dateSlot?.format(YYYY_MM_DD_FORMAT) + " " + timeSlot?.[0].format(HH_MM_SS_FORMAT))
      const end = dayjs(dateSlot?.format(YYYY_MM_DD_FORMAT) + " " + timeSlot?.[1].format(HH_MM_SS_FORMAT))
      values["timeSlot"] = [start, end]
    }
    try {
      let response
      if (isEditable) {
        response = await updateTask({
          taskId: leadDetailInfo?.tasks?.[0]?.id,
          encodedFile: values?.attachment,
          ...values,
        }).unwrap()
      } else {
        response = await addTask({
          lead,
          encodedFile: values?.attachment,
          verifyMeeting,
          ...values,
        })
          .unwrap()
          .then(() => {
            notification.success({
              message: isEditable ? t("Task Updated Successfully") : t("Task Added Successfully"),
            })
          })
      }

      if (!response?.error) {
        formRef?.current?.setVisible(false)

        resetErrorForAddTask()
        fireEvent({
          ...ACTION_TYPES.task_tab_submit_task,
          ...from,
          response: "200",
          client_id: client.id,
          lead_id: lead.id,
          ...eventPayload,
        })
        setBusySlotVisible(false)
      }
    } catch (e) {
      notification.error({ message: e?.message })
      fireEvent({
        ...ACTION_TYPES.task_tab_submit_task,
        ...from,
        response: e.status,
        client_id: client.id,
        lead_id: lead.id,
        ...eventPayload,
      })
    }
  }

  const checkEventList = selectedTime => {
    for (let i = 0; i < getCalendarEventsList?.length; i++) {
      let startTime = dayjs(getCalendarEventsList[i].startTimeStamp).unix()
      let endTime = dayjs(getCalendarEventsList[i].endTimeStamp).unix()
      if (startTime <= selectedTime[0].unix() && endTime >= selectedTime[1].unix()) {
        setBusySlotVisible(true)
        break
      }
    }
  }

  const isTaskInQCState = useMemo(
    () => leadDetailInfo?.tasks?.find(key => key.QualityCheckTask?.status === "pending"),
    [leadDetailInfo?.tasks]
  )

  const taskChildren = useMemo(
    () =>
      taskTypes?.leadStatuses
        ?.find(e => e.id === task)
        ?.children.map(e => {
          if (e?.id === taskChildrenMap["meeting_pushed"] && !!isTaskInQCState) {
            return {
              ...e,
              disabled: true,
            }
          }
          return e
        }),
    [task, isTaskInQCState]
  )

  const taskSubChildren = useMemo(() => {
    const subchildren = taskChildren?.find(e => e.id == subTask)?.children
    if (
      subchildren?.find(
        e =>
          e?.key === "sale_event" ||
          e?.key === "sale_event_meeting_arranged" ||
          e?.key === "sale_event_meeting_pushed" ||
          e?.key === "sale_event_meeting_attempted"
      ) &&
      !salesEvents?.saleEvents?.length
    ) {
      return subchildren?.filter(
        e =>
          e?.key !== "sale_event" &&
          e?.key !== "sale_event_meeting_arranged" &&
          e?.key !== "sale_event_meeting_pushed" &&
          e?.key !== "sale_event_meeting_attempted"
      )
    }
    return subchildren
  }, [subTask, taskChildren, salesEvents])

  const locations = useMemo(() => {
    return mapKeysToValues(taskSubChildren)
  }, [taskSubChildren])

  const renderAdditionalFields = useMemo(() => {
    const isRequiredSubtask = [
      taskChildrenMap["meeting_done"],
      taskChildrenMap["meeting_attempted"],
      taskChildrenMap["walk_in"],
    ].includes(subTask)
    if (!isRequiredSubtask) return false
    if (
      [taskChildrenMap["walk_in"], locations["company_office"], locations["company_office_meeting_attempted"]].includes(
        subofSubTask
      ) ||
      subTask === taskChildrenMap["walk_in"]
    ) {
      return "companyOffice"
    }
    if ([locations["outdoor"], locations["outdoor_meeting_attempted"]].includes(subofSubTask)) {
      return "plusCode"
    }
    if ([locations["site_office"], locations["site_office_meeting_attempted"]].includes(subofSubTask)) {
      return "visitedProjects"
    }
    return false
  }, [subTask, subofSubTask])

  const subLocations = useMemo(
    () => taskSubChildren?.find(e => e.id === subofSubTask)?.children,
    [subofSubTask, taskSubChildren]
  )
  const renderDayWiseTodoCount = data => {
    return (
      <Flex justify="space-between" gap="6px" style={{ marginTop: 10 }}>
        {data?.dateWiseCounts?.map(todo => (
          <Tooltip
            className="flexGrow"
            title={
              <Flex gap="4px" align="normal" vertical>
                <Flex gap="16px" justify="space-between" className="mbe-4" style={{ minWidth: 220 }}>
                  <IconText
                    icon={"IconTaskList"}
                    title={t("Todos Breakdown")}
                    iconProps={{ color: todo?.color }}
                    className="bold"
                    gap="6px"
                  />
                  <span className="semiBold" style={{ color: todo?.color }}>
                    {todo?.total}
                  </span>
                </Flex>

                <Flex justify="space-between">
                  <Text type="secondary">{t("Calls")}</Text>
                  {todo?.calls}
                </Flex>
                <Flex justify="space-between">
                  <Text type="secondary">{t("Sales")}</Text>
                  {todo?.sales}
                </Flex>
                <Flex justify="space-between">
                  <Text type="secondary">{t("Meetings")}</Text>
                  {todo?.meetings}
                </Flex>
              </Flex>
            }
          >
            <Card className={styles.deadlineCards} bodyStyle={{ "--ant-padding-lg": "2px" }}>
              <Flex vertical className="w-100" gap="2px">
                <Text type="secondary" className={styles.month}>
                  {todo?.month}
                </Text>
                <Text className={styles.day}>{todo?.day}</Text>
                <Tag type="light" color={todo?.color} className={styles.tag}>
                  <IconText gap="4px" align="middle" icon={"IconTaskList"} title={todo?.total} />
                </Tag>
              </Flex>
            </Card>
          </Tooltip>
        ))}
      </Flex>
    )
  }

  const suggestedComments = useMemo(() => {
    let commentsToShow = leadDetailInfo?.tasks?.filter(e => !!e.description).map(e => e?.description)
    return commentsToShow?.slice(-3)
  }, [leadDetailInfo?.tasks])

  const handleLocateMe = () => {
    if (!navigator?.geolocation) {
      notification?.error({ message: t("Geolocation not supported by your browser") })
      return
    }
    navigator.geolocation.getCurrentPosition(
      position => {
        const { latitude, longitude } = position?.coords
        const olc = new OpenLocationCode()
        const code = olc?.encode(latitude, longitude)
        setFieldValue("plusCode", code)
      },
      error => {
        notification?.error({ message: error?.message })
      },
      {
        enableHighAccuracy: true,
        maximumAge: 0,
      }
    )
  }

  const getPlusCodeTooltip = () => (
    <>
      <Legend title={t("How to Get Plus Code")} />
      <ul style={{ paddingLeft: 18, marginBottom: 12, color: theme["gray900"] }}>
        <li>{t("Find the Plus Code by tapping the location on Google Maps.")}</li>
        <li>{t("Look for the code, like G9H4+Q6, usually below the address, as shown in the image.")}</li>
        <li>{t("On web, tap the copy icon; on mobile, simply tap the Plus Code to copy.")}</li>
      </ul>
      <img src={plusCodeImage} style={{ borderRadius: "4px", marginBottom: 12 }} alt="Plus Code Example" />
      <Text>{t("For more information,")}</Text>{" "}
      <Link
        target="_blank"
        to="https://support.google.com/maps/answer/7047426?hl=en&sjid=1947056700739969908-NC&co=GENIE.Platform%3DAndroid&oco=1"
        className="pointer"
        style={{ color: theme["primary-color"] }}
      >
        {t(" visit google maps help")}
      </Link>
    </>
  )

  const dateFormItem = (
    <Form.Item
      name="completionDate"
      label={!!renderStartEndTime ? t("Meeting Date") : t("Completion Date")}
      required
      rules={
        !!salesEvent
          ? rules
          : rules.concat({
              validator: async (_, value) => {
                if (value > dayjs()) {
                  return Promise.reject(t(`The ${!!renderStartEndTime ? "meeting" : "completion"} date is invalid`))
                }
                return Promise.resolve()
              },
            })
      }
    >
      <DatePicker
        format={DATE_FORMAT}
        showTime={false}
        range={{
          endDate: dayjs().add(0, "days"),
          startDate: dayjs()
            .subtract(configs?.allowBackDateForTaskCompletion ? 1 : 0, "days")
            .startOf("day"),
        }}
        onSelect={item => {
          if (dayjs(item).isSame(dayjs(), "day")) {
            if (item > dayjs()) {
              setFieldsValue({ completionDate: dayjs() })
            }
          }
        }}
        onChange={item => getSalesEvents({ afterTime: dayjs(item).toISOString(), eventTypes: "sale" })}
        disableFutureTime={true}
        disabled={isEditable}
        getPopupContainer={trigger => trigger.parentNode}
      />
    </Form.Item>
  )

  const timeFormItem = (
    <Form.Item
      name={!!renderStartEndTime ? "meetingTime" : "completionTime"}
      required
      rules={rules}
      label={!!renderStartEndTime ? t("Meeting Time") : t("Completion Time")}
    >
      <TimeRangeSelector isRangePicker={!!renderStartEndTime} startDate={completionDate} enableAllTimesToday={true} />
    </Form.Item>
  )

  return (
    <div>
      <Modal
        title={isEditable ? t("Edit Task") : t("Add Task")}
        ref={formRef}
        okText={t("Submit")}
        width={780}
        cancelText={t("Cancel")}
        loading={isLoadingForUpdateTask || isLoading}
        onOk={form.submit}
        onModalVisible={onModalVisible}
        footerAlert={error}
        destroyOnClose={true}
        scrollToFirstError
        onCancel={() => {
          recorderControls?.stopRecording()
          setBusySlotVisible(false)
          resetErrorForAddTask()
          formRef?.current?.setVisible(false)
        }}
        disableOkButton={
          isEditable && leadDetailInfo?.inquiryStatusDetails?.taskStatusParent === tasksTypesMap["sales"]
        } // TODO LATER, needs to be changed
      >
        <Group>
          <ClientDetailBar client={client} lead={lead} project={project} leadDetailInfo={leadDetailInfo} />
          <Form
            form={form}
            layout="vertical"
            name="task-form"
            initialValues={getAddTaskInitialValues({ ...leadDetailInfo, Project: lead?.Project })}
            onFinish={onSubmit}
            scrollToFirstError
            preserve={false}
          >
            <Group>
              <div>
                {!!isTaskInQCState && <Alert type="error" message="Task is in QC state" />}
                {isEditable && leadDetailInfo?.inquiryStatusDetails?.taskStatusParent === tasksTypesMap["sales"] && (
                  <Alert message={t("Sales Task Cannot Be Edited")} type="error" showIcon className="mbe-4" />
                )}

                <Legend title={t("Current Task")} />
                <Group template="repeat(2, 1fr)" gap="6px 32px">
                  <Form.Item name="task" label={t("Task")} rules={rules} required>
                    <SelectInput
                      placeholder={t("Select task")}
                      options={taskTypes?.leadStatuses}
                      loading={isFetchingTaskTypes}
                      onChange={(value, option) => {
                        if (
                          option.subTask[0]?.key === "walk_in" ||
                          option.subTask[0]?.key === "meeting_done" ||
                          option.subTask[0]?.key === "meeting_attempted"
                        ) {
                          getCompanyOffices({ cityIds: userInfo?.City?.id?.toString() }, true)
                        }
                        setFieldsValue({
                          task: value,
                          subTask: option.subTask[0]?.id,
                          nextTask: (option.subTask[0].nextStatus || option.nextTask)?.id,
                        })
                      }}
                      disabled={isEditable}
                    />
                  </Form.Item>
                  {!!taskChildren?.length && (
                    <Form.Item
                      name="subTask"
                      label={t("Sub-Task")}
                      rules={rules.concat({
                        validator: async (_, value) => {
                          if (value === taskChildrenMap["meeting_pushed"] && !!isTaskInQCState) {
                            return Promise.reject("Cannot add task, in QC state")
                          }
                          return Promise.resolve()
                        },
                      })}
                      required
                    >
                      <SelectInput
                        placeholder={t("Select sub task")}
                        options={taskChildren}
                        onChange={(value, option) => {
                          setFieldsValue({
                            subTask: value,
                            subofSubTask: option.children[0]?.id,
                            nextTask: option.nextStatus?.id ? option.nextStatus?.id : -1,
                          })
                          if (
                            value === taskChildrenMap["meeting_arranged"] ||
                            value === taskChildrenMap["meeting_pushed"]
                          ) {
                            getCalendarEvents(getCalenderEventsObj)
                          }
                          if (
                            option.children[0]?.key === "company_office" ||
                            option.children[0]?.key === "company_office_meeting_attempted"
                          ) {
                            getCompanyOffices({ cityIds: userInfo?.City?.id?.toString() }, true)
                          }
                          const saleEvent = leadDetailInfo?.inquiry?.Client?.SalesEventClients?.find(
                            item =>
                              item?.SalesEventClientStatus?.key === "checked_in" ||
                              item?.SalesEventClientStatus?.key === "in_meeting"
                          )
                          if (
                            saleEvent &&
                            value == taskChildrenMap["meeting_done"] &&
                            dayjs().isSameOrBefore(dayjs(saleEvent?.SalesEvent?.endTimeStamp)) &&
                            dayjs().isSameOrAfter(dayjs(saleEvent?.SalesEvent?.startTimeStamp))
                          ) {
                            setDisableSaleEvent(true)
                            const subchildren = taskChildren
                              ?.find(e => e.id === value)
                              ?.children?.find(item => item?.key === "sale_event")
                            form.setFieldsValue({
                              salesEvent: saleEvent?.SalesEvent?.id,
                              subofSubTask: subchildren?.id,
                              subLocation: subchildren?.children?.[0]?.id,
                            })
                          } else setDisableSaleEvent(false)
                        }}
                        disabled={isEditable}
                      />
                    </Form.Item>
                  )}
                  {!!taskSubChildren?.length && (
                    <Form.Item
                      name="subofSubTask"
                      label={task === "pre_sale" ? t("Outcome") : t("Location")}
                      rules={rules.concat({
                        validator: (_, value) => {
                          if (!value)
                            return Promise.reject(
                              `${task === "pre_sale" ? t("Outcome") : t("Location")} ${t("task is required")}`
                            )
                          return Promise.resolve()
                        },
                      })}
                      required={task === "pre_sale"}
                    >
                      <SelectInput
                        placeholder={task === "pre_sale" ? t("Select outcome") : t("Select location")}
                        options={taskSubChildren}
                        onChange={(value, option) => {
                          form.resetFields(["attachment"])
                          if (option?.key === "company_office" || option?.key === "company_office_meeting_attempted") {
                            getCompanyOffices({ cityIds: userInfo?.City?.id?.toString() }, true)
                          } else if (option?.key === "site_office" || option?.key === "site_office_meeting_attempted") {
                            getProjectsByCity(userInfo?.City?.id?.toString(), true)
                          }
                          setFieldsValue({
                            subofSubTask: value,
                            subLocation: option.children[0]?.id,
                            nextTask: option?.nextStatus?.id,
                          })
                        }}
                        disabled={isEditable || disableSaleEvent}
                      />
                    </Form.Item>
                  )}
                  {!isEditable && renderAdditionalFields === "companyOffice" && (
                    <Form.Item name="companyOffice" label={t("Company Office")} required rules={rules}>
                      <SelectInput
                        placeholder={t("Select company office")}
                        options={companyOffice}
                        inputLoading={isCompanyOfficesFetching}
                        defaultValue={!!companyOffice?.length === 1 ? companyOffice?.[0]?.id : null}
                        onChange={(value, option) => {
                          setFieldsValue({ companyOffice: value })
                        }}
                        disabled={isEditable}
                      />
                    </Form.Item>
                  )}
                  {!isEditable && renderAdditionalFields === "visitedProjects" && (
                    <Form.Item name="visitedProject" label={t("Visited Project")} required rules={rules}>
                      <SelectInput
                        placeholder={t("Select visited project")}
                        options={projectsByCity}
                        inputLoading={isFetchingProjectsByCity}
                        onChange={(value, option) => {
                          setFieldsValue({ visitedProject: value })
                        }}
                        disabled={isEditable}
                      />
                    </Form.Item>
                  )}
                  {!isEditable && renderAdditionalFields === "plusCode" && (
                    <Form.Item
                      name="plusCode"
                      label={
                        <>
                          {t("Plus Code")}
                          <Icon
                            icon="BsInfo"
                            color="#707070"
                            size="1rem"
                            style={{
                              backgroundColor: "#E6E6E6",
                              borderRadius: "1rem",
                              marginLeft: "6px",
                              verticalAlign: "middle",
                              "--icon-color": "#707070",
                            }}
                            overlayInnerStyle={{ width: "360px" }}
                            tooltipText={getPlusCodeTooltip()}
                          />
                        </>
                      }
                      required
                      rules={rules.concat({
                        validator: async (_, value) => {
                          if (value) {
                            const response = await getCoordinates(value)
                            if (response?.error) {
                              return Promise.reject(response?.error?.message)
                            }
                          }
                          return Promise.resolve()
                        },
                      })}
                    >
                      <TextInput
                        placeholder={t("Enter plus code")}
                        allowClear
                        onChange={e => form.validateFields["plusCode"]}
                        customSuffix={
                          !plusCode && (
                            <IconText
                              icon="BiCurrentLocation"
                              iconProps={{ color: theme["primary-color"], size: "1rem" }}
                              textType="secondary"
                              title={t("Locate Me")}
                              titleProps={{
                                className: "pointer",
                                onClick: () => {
                                  handleLocateMe()
                                },
                              }}
                            />
                          )
                        }
                      />
                    </Form.Item>
                  )}
                  {!!subLocations?.length && (
                    <Form.Item name="subLocation" label={t("Sub Location")} required rules={rules}>
                      <SelectInput
                        placeholder={t("Select sub location")}
                        options={subLocations}
                        onChange={(value, option) => {
                          setFieldsValue({ subLocation: value })
                        }}
                        disabled={isEditable}
                      />
                    </Form.Item>
                  )}
                  {subofSubTask &&
                    subofSubTask ===
                      taskSubChildren?.find(
                        item =>
                          item?.key === "sale_event" ||
                          item?.key === "sale_event_meeting_pushed" ||
                          item?.key === "sale_event_meeting_attempted" ||
                          item?.key === "sale_event_meeting_arranged"
                      )?.id && (
                      <Form.Item name="salesEvent" label={t("Sales Event")} required rules={rules}>
                        <SelectInput
                          placeholder={t("Select sales event")}
                          options={salesEvents?.saleEvents}
                          disabled={isEditable || disableSaleEvent || salesEvent}
                          inputLoading={isFetchingsSaleEvents}
                        />
                      </Form.Item>
                    )}

                  {!!renderStartEndTime ? (
                    <>
                      {dateFormItem}
                      {timeFormItem}
                    </>
                  ) : (
                    <Group template="repeat(2, 2fr)" gap="4px 32px">
                      {dateFormItem}
                      {timeFormItem}
                    </Group>
                  )}
                  <Form.Item
                    name="attachment"
                    label={t("Attachments")}
                    required={
                      subTask === taskChildrenMap["proposal_message"] ||
                      subTask === taskChildrenMap["video_meeting"] ||
                      subTask === taskChildrenMap["walk_in"] ||
                      (subTask === taskChildrenMap["im_proposal"] && subofSubTask === locations["im_proposal_shared"])
                        ? true
                        : false
                    }
                    rules={rules.concat({
                      validator: async (_, value) => {
                        if (
                          subTask === taskChildrenMap["proposal_message"] ||
                          subTask === taskChildrenMap["video_meeting"] ||
                          subTask === taskChildrenMap["walk_in"] ||
                          (subTask === taskChildrenMap["im_proposal"] &&
                            subofSubTask === locations["im_proposal_shared"] &&
                            value.length === 0)
                        ) {
                          return Promise.reject(t("Attachment is required"))
                        }
                        return Promise.resolve()
                      },
                    })}
                  >
                    {attachmentVerificationRequired ? (
                      <MeetingVerificationAttachmentUploader
                        name="attachment"
                        files={attachment}
                        setFiles={cb => {
                          form.setFieldsValue({ attachment: cb(attachment) })
                        }}
                        disabled={
                          isEditable &&
                          leadDetailInfo?.inquiryStatusDetails?.taskStatusParent === tasksTypesMap["sales"]
                        }
                      />
                    ) : (
                      <UploadDragger
                        name="attachment"
                        files={attachment}
                        setFiles={cb => {
                          form.setFieldsValue({ attachment: cb(attachment) })
                        }}
                        disabled={
                          isEditable &&
                          leadDetailInfo?.inquiryStatusDetails?.taskStatusParent === tasksTypesMap["sales"]
                        }
                      />
                    )}
                  </Form.Item>
                  {!isEditable && (
                    <Form.Item
                      name="voiceComment"
                      label={t("Add Voice Comment")}
                      rules={[
                        {
                          validator: (_, _value) => {
                            if (recorderControls?.isRecording) {
                              return Promise.reject("Recording is still in progress, press the save button")
                            }
                            return Promise.resolve()
                          },
                        },
                      ]}
                    >
                      <VoiceComment
                        getController={getController}
                        setVoice={voice => {
                          setFieldsValue({ voiceComment: voice })
                        }}
                      />
                    </Form.Item>
                  )}
                  <Form.Item
                    className={cx(!!suggestedComments?.length && styles.commentInput, "span-all")}
                    name="comment"
                    label={t("Comment")}
                    rules={rules}
                    required
                    extra={
                      !!suggestedComments?.length && (
                        <Group gap="8px" className={cx("p-12")} template="auto 1fr">
                          <Text type="secondaryLight">{t("Suggested Comments")}:</Text>
                          <Row gutter={[8, 8]}>
                            {suggestedComments?.map(element => (
                              <Tag
                                type="light"
                                color="#A7A7A7"
                                onClick={() => handleSuggestedCommentClick(element)}
                                bordered
                                pill
                                maxLength={element.length > 50 ? 29 : undefined}
                                tooltipText={element.length > 50 && element}
                              >
                                <Icon icon="HiPlus" size={12} style={{ marginInlineEnd: 4 }} />
                                {element}
                              </Tag>
                            ))}
                          </Row>
                        </Group>
                      )
                    }
                  >
                    <TextInput
                      placeholder={t("Enter comment")}
                      maxLength={1000}
                      lineCount={3}
                      disabled={
                        isEditable && leadDetailInfo?.inquiryStatusDetails?.taskStatusParent === tasksTypesMap["sales"]
                      }
                    />
                  </Form.Item>
                </Group>
              </div>

              <div>
                <Legend title={t("Next Task")} hidden={subTask === taskChildrenMap["mou"] ? true : false} />{" "}
                <Group template="repeat(2, 1fr)" gap="6px 32px">
                  <Form.Item
                    required
                    hidden={subTask === taskChildrenMap["mou"] ? true : false}
                    name="nextTask"
                    label={t("Next Task")}
                    rules={rules.concat({
                      validator: async (_, value) => {
                        if (subTask === taskChildrenMap["mou"] || value === 0) {
                          return Promise.resolve()
                        } else if (!value) return Promise.reject(t("Next task is required"))
                      },
                    })}
                  >
                    <SelectInput
                      placeholder={t("Select next task")}
                      options={taskTypes?.nextStatuses}
                      getOptionLabel={option => option?.statusTitleTodo}
                      onChange={(value, _option) => {
                        setFieldsValue({ nextTask: value })
                      }}
                      disabled={
                        isEditable && leadDetailInfo?.inquiryStatusDetails?.taskStatusParent === tasksTypesMap["sales"]
                      }
                    />
                  </Form.Item>
                  <Form.Item
                    name="deadlineWrapper"
                    extra={renderDayWiseTodoCount(todosCount)}
                    hidden={subTask === taskChildrenMap["meeting_pushed"] || nextTask === nextTasksMap["meeting_done"]}
                  >
                    <Group template="repeat(2, 2fr)" gap="4px 32px">
                      <Form.Item
                        name="deadLine"
                        label={t("Deadline")}
                        required={true}
                        rules={rules}
                        hidden={
                          subTask === taskChildrenMap["meeting_pushed"] || nextTask === nextTasksMap["meeting_done"]
                        }
                      >
                        <DatePicker
                          range={{ startDate: dayjs() }}
                          showTime={false}
                          disablePastTime={true}
                          format={DATE_FORMAT}
                          disabled={
                            isEditable &&
                            leadDetailInfo?.inquiryStatusDetails?.taskStatusParent === tasksTypesMap["sales"]
                          }
                          getPopupContainer={trigger => trigger.parentNode}
                        />
                      </Form.Item>
                      <Form.Item
                        name="deadLineTime"
                        required
                        rules={rules}
                        label={t("Deadline Time")}
                        hidden={
                          subTask === taskChildrenMap["meeting_pushed"] || nextTask === nextTasksMap["meeting_done"]
                        }
                      >
                        <TimeRangeSelector
                          startDate={deadline}
                          isRangePicker={false}
                          disabled={
                            isEditable &&
                            leadDetailInfo?.inquiryStatusDetails?.taskStatusParent === tasksTypesMap["sales"]
                          }
                        />
                      </Form.Item>
                    </Group>
                  </Form.Item>
                  <Form.Item name="project" label={t("Project")}>
                    <SelectInput
                      disabled={!!leadDetailInfo?.contracts.length || isEditable}
                      placeholder={t("Select project")}
                      options={projects}
                      getOptionValue={option => option.projectId}
                      onChange={(value, _option) => {
                        setFieldsValue({ project: _option })
                      }}
                    />
                  </Form.Item>
                  <Form.Item
                    name="leadsClassification"
                    label={t("Leads Classification")}
                    required={!(userInfo?.UserDesignation?.name === "TSR")}
                    rules={rules}
                    disabled={!(userInfo?.UserDesignation?.name === "TSR")}
                  >
                    <RadioButtons
                      options={classifications}
                      disabled={userInfo?.UserDesignation?.name === "TSR" || isEditable}
                    />
                  </Form.Item>

                  <Form.Item
                    name="tsrClassification"
                    label={t("TSR Classification")}
                    required
                    hidden={!(userInfo?.UserDesignation?.name === "TSR")}
                  >
                    <RadioButtons options={classifications} disabled={isEditable} />
                  </Form.Item>

                  {!!(subTask === taskChildrenMap["meeting_pushed"] || nextTask === nextTasksMap["meeting_done"]) && (
                    <div>
                      <Form.Item
                        name="dateSlot"
                        label={t("Date Slot")}
                        required={true}
                        rules={rules}
                        extra={renderDayWiseTodoCount(todosCount)}
                      >
                        <DatePicker range={{ startDate: dayjs() }} format={DATE_FORMAT} showTime={false} />
                      </Form.Item>

                      <Form.Item
                        name="timeSlot"
                        label={t("Time Slot")}
                        required={true}
                        rules={rules}
                        extra={
                          <Text type="warning" hidden={!busySlotVisible}>
                            {t("User has already scheduled a meeting during this time")}
                          </Text>
                        }
                      >
                        <TimeRangeSelector
                          startDate={dateSlot || dayjs()}
                          onChange={x => {
                            setBusySlotVisible(false)
                            checkEventList(x)
                          }}
                        />
                      </Form.Item>
                    </div>
                  )}
                </Group>
              </div>
            </Group>
          </Form>
        </Group>
      </Modal>
    </div>
  )
}
